import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Terminal as XTerm } from "@xterm/xterm";
import { FitAddon } from "@xterm/addon-fit";
import "@xterm/xterm/css/xterm.css";
import { API_BASE_URL } from "../../constants/configuration";
import { Power, RotateCw, Maximize2, Minimize2 } from "lucide-react";
import "./terminal.scss";

function wsUrlFromApiBase() {
  // API_BASE_URL is e.g. "http://localhost:8003/" or
  // "https://access.shtops.xyz/server/". We MUST preserve the pathname
  // prefix — otherwise a reverse-proxy mount like `location /server/`
  // is bypassed and the WS upgrade lands on a path nginx doesn't know.
  const u = new URL(API_BASE_URL);
  const proto = u.protocol === "https:" ? "wss:" : "ws:";
  // Strip trailing `/` so the caller's `/ws/terminal/...` doesn't double up.
  const prefix = u.pathname.replace(/\/$/, "");
  return `${proto}//${u.host}${prefix}`;
}

export default function TerminalPage() {
  const { projectId, serverId } = useParams();
  const [params] = useSearchParams();
  const label = params.get("label") || "terminal";
  const host = params.get("host") || "";
  // Optional start directory (set by the Deploy page's per-app Terminal
  // button). Shown in the bar so the target is visible before the shell
  // has finished connecting.
  const cwd = params.get("cwd") || "";
  const navigate = useNavigate();

  const pageRef = useRef(null);
  const containerRef = useRef(null);
  const termRef = useRef(null);
  const fitRef = useRef(null);
  const wsRef = useRef(null);
  const [status, setStatus] = useState("Connecting…");
  const [closed, setClosed] = useState(null); // { reason }
  // Bumping this key re-runs the effect → tear down the dead terminal/WS
  // and start a fresh one without leaving the page. Lets the operator
  // recover in place when the SSH session drops (sudo prompt timeout,
  // network blip, server-side close).
  const [reconnectKey, setReconnectKey] = useState(0);
  const [isFs, setIsFs] = useState(false);

  useEffect(() => {
    // Fresh session each (re)connect: reset status so the bar stops showing
    // the previous "Connection refused" while a new attempt is opening.
    setClosed(null);
    setStatus("Connecting…");
    const term = new XTerm({
      cursorBlink: true,
      fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
      fontSize: 13,
      theme: { background: "#0b1220" },
      scrollback: 5000,
    });
    const fit = new FitAddon();
    term.loadAddon(fit);
    term.open(containerRef.current);
    // Fit on the next frame so the flex container has measured its full
    // height. A synchronous fit() right after open() often locks xterm to
    // the not-yet-final container size, leaving a big empty band below.
    requestAnimationFrame(() => { try { fit.fit(); } catch {} });
    termRef.current = term;
    fitRef.current = fit;
    // Keep refitting whenever the container changes size (window resize,
    // entering/leaving fullscreen, sidebar collapse, devtools toggle…).
    const ro = new ResizeObserver(() => { try { fit.fit(); } catch {} });
    ro.observe(containerRef.current);

    // Forward `pod`/`namespace`/`container` query params to the WS so the
    // backend opens the right shell:
    //   pod + namespace + (optional) container → kubectl exec into pod
    //   container only                         → docker exec into container
    //   none                                   → host shell (or sudo shell)
    const fwd = new URLSearchParams();
    const pod = params.get("pod");
    const ns = params.get("namespace");
    const container = params.get("container");
    if (pod) {
      fwd.set("pod", pod);
      if (ns) fwd.set("namespace", ns);
      if (container) fwd.set("container", container);
    } else if (container) {
      fwd.set("container", container);
    }
    // `cwd` is orthogonal to the three above — it applies to whichever shell
    // ends up opening, so it sits outside the if/else chain. The backend
    // validates it and `cd`s once the shell is live.
    if (cwd) fwd.set("cwd", cwd);
    const query = fwd.toString() ? `?${fwd.toString()}` : "";
    const url = `${wsUrlFromApiBase()}/ws/terminal/${projectId}/${serverId}${query}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setStatus("Authenticating…");
      // Send initial size right after open
      const { rows, cols } = term;
      ws.send(JSON.stringify({ type: "resize", rows, cols }));
    };

    ws.onmessage = (e) => {
      let msg;
      try {
        msg = JSON.parse(e.data);
      } catch {
        return;
      }
      if (msg.type === "data") {
        term.write(msg.data);
      } else if (msg.type === "status") {
        setStatus(msg.message);
      } else if (msg.type === "close") {
        setClosed({ reason: msg.reason || "Disconnected" });
      }
    };

    ws.onclose = (e) => {
      // Stop accepting keypresses — they'd otherwise be silently dropped
      // because the send below is gated on ws.OPEN ("typing into nothing").
      try { term.options.disableStdin = true; } catch {}
      if (!closed) {
        // Map common reject codes from our upgrade handler.
        const map = {
          1006: "Connection refused or closed before handshake",
        };
        setClosed({
          reason:
            e.reason ||
            map[e.code] ||
            (e.code === 1008
              ? "Unauthorized — please log in again"
              : `Disconnected (code ${e.code})`),
        });
      }
    };

    ws.onerror = () => {
      setStatus("Connection error");
    };

    term.onData((d) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "data", data: d }));
      }
    });

    term.onResize(({ rows, cols }) => {
      if (ws.readyState === WebSocket.OPEN) {
        ws.send(JSON.stringify({ type: "resize", rows, cols }));
      }
    });

    // ResizeObserver above already covers resize; keep window listener for
    // older browsers that don't fire RO during fullscreen transitions.
    const onWindowResize = () => { try { fit.fit(); } catch {} };
    window.addEventListener("resize", onWindowResize);

    return () => {
      window.removeEventListener("resize", onWindowResize);
      try { ro.disconnect(); } catch {}
      try { ws.close(); } catch {}
      term.dispose();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, serverId, reconnectKey]);

  const toggleFullscreen = async () => {
    const el = pageRef.current;
    if (!el) return;
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
      } else {
        await el.requestFullscreen();
      }
    } catch {
      // Some browsers reject without a user gesture or in iframes — ignore.
    }
  };
  // Track fullscreen state so the icon label reflects it.
  useEffect(() => {
    const onFs = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, []);

  return (
    <div ref={pageRef} className="terminal-page">
      <div className="terminal-bar">
        <div className="left">
          <button
            className="btn btn-sm btn-light"
            onClick={() => navigate("/projects")}
          >
            ← Back
          </button>
          <strong className="ms-3">{label}</strong>
          {host && <code className="ms-2 host">{host}</code>}
          {cwd && <code className="ms-2 host" title="Starting directory">{cwd}</code>}
        </div>
        <div className="right">
          {closed ? (
            <>
              <span className="status closed me-2 d-inline-flex align-items-center gap-1"><Power size={12} /> {closed.reason}</span>
              <button
                type="button"
                className="btn btn-sm btn-primary me-2"
                onClick={() => setReconnectKey((k) => k + 1)}
              >
                <RotateCw size={14} className="me-1" /> Reconnect
              </button>
            </>
          ) : (
            <span className="status me-2">{status}</span>
          )}
          <button
            type="button"
            className="btn btn-sm btn-light"
            onClick={toggleFullscreen}
            title={isFs ? "Exit fullscreen" : "Fullscreen"}
          >
            {isFs
              ? <span className="d-inline-flex align-items-center gap-1"><Minimize2 size={14} /> Exit</span>
              : <span className="d-inline-flex align-items-center gap-1"><Maximize2 size={14} /> Full</span>}
          </button>
        </div>
      </div>
      <div ref={containerRef} className="terminal-host" />
    </div>
  );
}
